import type { Caractere, PalavraCanonica } from '../tipos';
import {
  TAMANHO_MAX_AUTOMATICA,
  TAMANHO_MAX_JOGADOR,
  TAMANHO_MIN_AUTOMATICA,
  ehLetraJogavel,
} from './alfabeto';
import { caracteres, paraCanonica, paraComparacao } from './normalizacao';

/**
 * Índice léxico em memória (§16: pronto em até 1,5 s).
 *
 * Duas responsabilidades distintas:
 *
 *  1. **Validação** — a palavra digitada existe? Qualquer tamanho de 1 a 12.
 *  2. **Sugestão** — quais palavras automáticas têm a letra X na posição N?
 *     Só entram aqui as candidatas de 3 a 10 caracteres (§4.2).
 *
 * A compilação é separada do acesso de propósito: `DadosIndice` é composto
 * apenas por estruturas clonáveis (`Set`, `Map`, `Uint32Array`), então o worker
 * compila e envia por `postMessage`, e o thread principal só embrulha em
 * `indiceDeDados`. Ver ADR-0005.
 */

/** Payload transferível do worker para o thread principal. */
export interface DadosIndice {
  /** Todas as palavras, para validação (§4.1). */
  readonly conjunto: ReadonlySet<string>;
  /**
   * Forma sem diacrítico das palavras que **têm** diacrítico (§23.3).
   *
   * Só delas: quem não tem acento já está em `conjunto` com a mesma grafia, e
   * duplicar as 330 mil dobraria a memória do índice para nada. É o suficiente
   * para a Trama aceitar `AVIAO` por `AVIÃO`.
   */
  readonly conjuntoDobrado: ReadonlySet<string>;
  /** Só as candidatas automáticas (§4.2), indexadas por posição. */
  readonly candidatas: readonly string[];
  /** Chave `"<letra> <posição>"` → índices em `candidatas`. */
  readonly porChave: ReadonlyMap<string, Uint32Array>;
  readonly posicoesPorLetra: ReadonlyMap<string, readonly number[]>;
  /** Letras presentes em alguma palavra — teste exato da última regra de §9. */
  readonly letrasComPalavra: ReadonlySet<string>;
  /**
   * Para cada letra, o menor "resto à direita" observado em cada posição.
   *
   * `alcancePorLetra.get('G')[e]` é, entre todas as palavras que têm `G` na
   * posição `e`, a menor quantidade de letras depois dele — ou `SEM_ALCANCE`
   * se nenhuma palavra tem `G` ali. É o que permite responder, em tempo
   * constante, à última regra de §9: cabe alguma palavra numa célula com
   * tanto espaço livre à esquerda e à direita?
   */
  readonly alcancePorLetra: ReadonlyMap<string, Uint8Array>;
}

/** Marca de "não existe palavra com esta letra nesta posição". */
export const SEM_ALCANCE = 255;

export interface IndiceLexico {
  readonly totalPalavras: number;
  readonly totalCandidatas: number;
  /** Validação léxica (§4.1). */
  existe(palavra: PalavraCanonica): boolean;
  /**
   * A palavra existe, ignorando acentos? (§23.3)
   *
   * É a validação da Trama, e só dela. Lá o jogador digita sem acento —
   * é o que a dedução permite —, então `AVIAO` precisa ser reconhecido por
   * `AVIÃO`. No jogo em cadeia a regra continua sendo §4.3, com o diacrítico
   * preservado.
   */
  existeDobrado(palavra: string): boolean;
  /** Candidatas automáticas com `letra` exatamente em `posicao` (§4.2). */
  candidatas(letra: Caractere, posicao: number): readonly PalavraCanonica[];
  /**
   * Acesso indexado sem materializar a lista. É o caminho quente da geração de
   * sugestões: um `A` na posição 1 pode ter dezenas de milhares de candidatas,
   * e o orçamento por tecla é de 100 ms no p95 (§16).
   */
  candidataEm(letra: Caractere, posicao: number, i: number): PalavraCanonica | undefined;
  /** Posições em que `letra` aparece em alguma candidata. */
  posicoesDe(letra: Caractere): readonly number[];
  /** Quantas candidatas existem para (letra, posição). */
  quantidade(letra: Caractere, posicao: number): number;
  /**
   * Existe alguma palavra do dicionário (qualquer tamanho) que contenha `letra`?
   * Condição necessária, não suficiente: veja `cabeComFolga`.
   */
  temPalavraComLetra(letra: Caractere): boolean;
  /**
   * Existe palavra com `letra` numa posição que caiba no espaço disponível?
   *
   * `folgaEsquerda` e `folgaDireita` são quantas letras a horizontal futura
   * pode ter de cada lado da célula sem violar §9. Uma folga negativa
   * significa que nem a própria letra cabe ali.
   *
   * É o teste exato da última regra de §9. Sem ele, uma letra da ponte é
   * oferecida como âncora só por existir no dicionário, e o jogador escolhe
   * uma célula onde nenhuma palavra pode ser escrita.
   */
  cabeComFolga(letra: Caractere, folgaEsquerda: number, folgaDireita: number): boolean;
  /**
   * Candidatas com exatamente `tamanho` letras, na ordem do corpus.
   *
   * É o sorteio da Trama (§23.2): as respostas do dia saem das palavras
   * correntes, que é o mesmo recorte que o jogo já usa para sugerir — quem é
   * frequente o bastante para aparecer no tabuleiro é frequente o bastante
   * para ser adivinhado. A ordem é a do artefato, que é versionado, e por isso
   * a escolha do dia é reproduzível.
   */
  candidatasDeTamanho(tamanho: number): readonly PalavraCanonica[];
}

function chave(letra: string, posicao: number): string {
  return `${letra} ${posicao}`;
}

/** Uma palavra pode ser sugerida verticalmente pelo motor? (§4.2) */
export function ehCandidataAutomatica(palavra: PalavraCanonica): boolean {
  const tamanho = caracteres(palavra).length;
  return tamanho >= TAMANHO_MIN_AUTOMATICA && tamanho <= TAMANHO_MAX_AUTOMATICA;
}

/**
 * Compila as listas de palavras nas estruturas do índice.
 *
 * `palavras` é tudo o que o jogo **reconhece** (§4.1). `sugeriveis`, quando
 * informado, é o subconjunto que ele pode **sugerir** (§4.2) — um corpus
 * ortográfico reconhece milhares de termos técnicos que ninguém quer ver
 * ocupando o tabuleiro. Omitido, toda palavra no tamanho certo é candidata,
 * que é o comportamento de um corpus pequeno e curado como a amostra.
 *
 * Função pura e síncrona. Em produção roda dentro do worker; nos testes, direto.
 */
export function compilarDados(
  palavras: readonly PalavraCanonica[],
  sugeriveis?: readonly PalavraCanonica[],
): DadosIndice {
  const permitidas =
    sugeriveis === undefined ? undefined : new Set(sugeriveis.map((p) => paraCanonica(p)));

  const conjunto = new Set<string>();
  const conjuntoDobrado = new Set<string>();
  const candidatas: string[] = [];
  const acumulador = new Map<string, number[]>();
  const posicoesPorLetra = new Map<string, Set<number>>();
  const letrasComPalavra = new Set<string>();
  const alcancePorLetra = new Map<string, Uint8Array>();

  for (const bruta of palavras) {
    // O artefato do pipeline já vem canônico (NFC, caixa-alta): normalizar de
    // novo custaria duas passadas por palavra, e são centenas de milhares.
    // Só o que destoa paga o preço da conversão.
    const palavra =
      bruta === (bruta as string).toUpperCase() ? bruta : (paraCanonica(bruta) as PalavraCanonica);
    if (conjunto.has(palavra)) continue;
    conjunto.add(palavra);

    const dobrada = paraComparacao(palavra);
    if (dobrada !== palavra) conjuntoDobrado.add(dobrada);

    const letras = caracteres(palavra);
    for (const letra of letras) {
      if (ehLetraJogavel(letra)) letrasComPalavra.add(letra);
    }

    // Alcance: toda palavra do jogador conta, de 1 a 12 letras (§4.1), e não
    // apenas as candidatas automáticas.
    if (letras.length <= TAMANHO_MAX_JOGADOR) {
      for (let i = 0; i < letras.length; i += 1) {
        const letra = letras[i];
        if (letra === undefined || !ehLetraJogavel(letra)) continue;

        let alcance = alcancePorLetra.get(letra);
        if (alcance === undefined) {
          alcance = new Uint8Array(TAMANHO_MAX_JOGADOR).fill(SEM_ALCANCE);
          alcancePorLetra.set(letra, alcance);
        }
        const direita = letras.length - 1 - i;
        if (direita < (alcance[i] as number)) alcance[i] = direita;
      }
    }

    if (!ehCandidataAutomatica(palavra)) continue;
    if (permitidas !== undefined && !permitidas.has(palavra)) continue;

    const indiceCandidata = candidatas.length;
    candidatas.push(palavra);

    for (let posicao = 0; posicao < letras.length; posicao += 1) {
      const letra = letras[posicao];
      // Hifens nunca são ponto de cruzamento (§4.1, §5.3).
      if (letra === undefined || !ehLetraJogavel(letra)) continue;

      const k = chave(letra, posicao);
      const lista = acumulador.get(k);
      if (lista === undefined) acumulador.set(k, [indiceCandidata]);
      else lista.push(indiceCandidata);

      const posicoes = posicoesPorLetra.get(letra);
      if (posicoes === undefined) posicoesPorLetra.set(letra, new Set([posicao]));
      else posicoes.add(posicao);
    }
  }

  // Compacta para Uint32Array: mesma informação, uma fração da memória, e
  // clonável sem custo perceptível na fronteira do worker.
  const porChave = new Map<string, Uint32Array>();
  for (const [k, lista] of acumulador) porChave.set(k, Uint32Array.from(lista));
  acumulador.clear();

  const posicoesOrdenadas = new Map<string, readonly number[]>();
  for (const [letra, posicoes] of posicoesPorLetra) {
    posicoesOrdenadas.set(
      letra,
      [...posicoes].sort((a, b) => a - b),
    );
  }

  return {
    conjunto,
    conjuntoDobrado,
    candidatas,
    porChave,
    posicoesPorLetra: posicoesOrdenadas,
    letrasComPalavra,
    alcancePorLetra,
  };
}

/** Embrulha os dados compilados na interface de consulta. Custo O(1). */
export function indiceDeDados(dados: DadosIndice): IndiceLexico {
  return {
    totalPalavras: dados.conjunto.size,
    totalCandidatas: dados.candidatas.length,

    existe(palavra) {
      return dados.conjunto.has(paraCanonica(palavra));
    },

    existeDobrado(palavra) {
      const dobrada = paraComparacao(palavra);
      return dados.conjunto.has(dobrada) || dados.conjuntoDobrado.has(dobrada);
    },

    candidatas(letra, posicao) {
      const ids = dados.porChave.get(chave(letra, posicao));
      if (ids === undefined) return [];
      const saida: PalavraCanonica[] = new Array(ids.length);
      for (let i = 0; i < ids.length; i += 1) {
        saida[i] = dados.candidatas[ids[i] as number] as PalavraCanonica;
      }
      return saida;
    },

    candidataEm(letra, posicao, i) {
      const ids = dados.porChave.get(chave(letra, posicao));
      if (ids === undefined || i < 0 || i >= ids.length) return undefined;
      return dados.candidatas[ids[i] as number] as PalavraCanonica | undefined;
    },

    posicoesDe(letra) {
      return dados.posicoesPorLetra.get(letra) ?? [];
    },

    candidatasDeTamanho(tamanho) {
      const saida: PalavraCanonica[] = [];
      for (const candidata of dados.candidatas) {
        if (caracteres(candidata).length === tamanho) saida.push(candidata as PalavraCanonica);
      }
      return saida;
    },

    quantidade(letra, posicao) {
      return dados.porChave.get(chave(letra, posicao))?.length ?? 0;
    },

    temPalavraComLetra(letra) {
      return dados.letrasComPalavra.has(letra);
    },

    cabeComFolga(letra, folgaEsquerda, folgaDireita) {
      if (folgaEsquerda < 0 || folgaDireita < 0) return false;
      const alcance = dados.alcancePorLetra.get(letra);
      if (alcance === undefined) return false;

      const limite = Math.min(folgaEsquerda, alcance.length - 1);
      for (let e = 0; e <= limite; e += 1) {
        const direita = alcance[e] as number;
        if (direita !== SEM_ALCANCE && direita <= folgaDireita) return true;
      }
      return false;
    },
  };
}

/** Atalho para testes e para o caminho síncrono. */
export function construirIndice(palavras: readonly PalavraCanonica[]): IndiceLexico {
  return indiceDeDados(compilarDados(palavras));
}

/** Índice vazio — usado enquanto o dicionário carrega e em testes de borda. */
export const INDICE_VAZIO: IndiceLexico = construirIndice([]);
